import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import ContentLoader from "@/common/components/loaders/ContentLoader";
import NoProjectAccessPage from "@/common/components/NoProjectAccessPage";
import SessionExpiredOverlay from "@/common/components/SessionExpiredOverlay";
import { useAuth } from "@/common/hooks/useAuth";
import GlobalLayout from "@/common/layouts/GlobalLayout";
import { useGetUserOrgs } from "@/common/services/users/users.queries";
import {
  hasSelectedOrgThisSession,
  markOrgSelectedForSession,
} from "@/common/utils/orgSession";

export const ProtectedRoute = () => {
  const {
    isAuthenticated,
    isSuperAdmin,
    isAppReady,
    isBootstrappingFromHint,
    userPreferenceData,
    isUserPreferenceLoading,
  } = useAuth();
  const location = useLocation();
  const { data: orgsData, isLoading: isOrgsLoading } = useGetUserOrgs();

  const renderAppShell = (content: ReactNode) => (
    <GlobalLayout>
      <SessionExpiredOverlay />
      {content}
    </GlobalLayout>
  );

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search + location.hash)}`}
        replace
      />
    );
  }

  // While the sessionStorage bootstrap hint is being validated against the
  // server, isAuthenticated is optimistically true but not yet confirmed.
  // Redirecting to /select-org (or any other route) at this point would cause
  // a spurious flash — or worse, a genuine wrong-destination navigate — before
  // the server response arrives. Hold here until the session is confirmed.
  // This is Chrome-specific: tab-restore preserves sessionStorage, so the
  // hint can survive across apparent "fresh" opens. See issue #2224.
  if (isBootstrappingFromHint) {
    return renderAppShell(<ContentLoader />);
  }

  // Multi-org gate: a user who belongs to more than one organization must pick
  // their active org at the start of every session before entering the app. The
  // Login form already routes here on an explicit sign-in, but returning users
  // with a still-valid session never pass through it and would silently land on
  // their last active org — so enforce the same rule for any authenticated
  // multi-org user who hasn't chosen an org yet this session.
  if (!isSuperAdmin && !hasSelectedOrgThisSession()) {
    const orgs = Array.isArray(orgsData?.data) ? orgsData.data : [];
    if (isOrgsLoading) {
      return renderAppShell(<ContentLoader />);
    }
    if (orgs.length > 1) {
      return <Navigate to="/select-org" replace />;
    }
    // Single-org (or zero-org/onboarding) user — mark the session so this
    // whole block is permanently skipped on every subsequent render/navigation.
    // Without this, the org check re-runs on every render because
    // hasSelectedOrgThisSession() stays false, and any transient moment where
    // orgsData is stale/empty can re-trigger the select-org redirect mid-session.
    markOrgSelectedForSession();
  }

  // Onboarding guard: authenticated users who haven't completed the workspace
  // wizard yet (no versionId in preferences) must finish onboarding before
  // accessing any protected page. Skip for super-admins.
  if (
    !isSuperAdmin &&
    !isUserPreferenceLoading &&
    userPreferenceData?.data !== undefined &&
    !userPreferenceData?.data?.versionId &&
    userPreferenceData?.data?.noProjectAccess !== true
  ) {
    return <Navigate to="/onboarding/wizard" replace />;
  }

  // User is authenticated but their only project was deleted — show a dedicated page
  if (
    !isSuperAdmin &&
    !isUserPreferenceLoading &&
    userPreferenceData?.data?.noProjectAccess === true
  ) {
    return <NoProjectAccessPage />;
  }

  return renderAppShell(isAppReady ? <Outlet /> : <ContentLoader />);
};
